import type { InjectionKey } from 'vue';
import type { Emitter } from 'mitt';
import type { IPortfolioTreeSubportfolio, IPortfolioTreeStrategy } from './IPortfolioTree';
import type { ISidebarRow } from './ISidebarRow';
import type { GlobalEventTypes } from '@/composables/useGlobalEventBus';
import type { IPcaOption } from '@/api-v2/web/pca';
import type { IRegressionResponse, PartialRegressionSetDTO } from '@/api-v2/web/regression';
import type { IItemUnderAnalysis } from './IItemUnderAnalysis';
import type { DSBTabOption } from '@/constants/DSBTabOption';
import type { CheckBeforeCalculateDraftParams } from './DraftCalculateMutationParams';
import type { BarraFactorGroupConstants } from '@/constants/BarraFactorGroupConstants';

export enum PortfolioControllerEvents {
  CHECK_BEFORE_CALCULATE_DRAFT = 'analytics:calculate',
  NAVIGATE_TO_CELL = 'navigate-to-cell',
  UPDATE_CHILDREN = 'update-children',
  FOCUS = 'focus',
  RESCALE_DOWN = 'RESCALE-DOWN',
  RESCALE_UP = 'RESCALE-UP',
  OPEN_SUBPORTFOLIO = 'open-subportfolio',
  TOGGLE_PIN = 'toggle-pin',
  CLEAR_PINNED = 'clear-pinned',
  ON_NAME_CLICK = 'on-name-click',
  CLEAR_FORCE_ITEM_UNDER_ANALYSIS = 'clear-force-item-under-analysis',
  EXPAND_NAVIGATION_PANEL = 'expand-navigation-panel',
  SET_PORTFOLIO_TREE_HAS_CHANGES = 'SET-PORTFOLIO-TREE-HAS-CHANGES',
  ADD_SUBPORTFOLIO_TO_TREE = 'ADD-SUBPORTFOLIO-TO-TREE',

  RESCALE = 'RESCALE',

  /**
   * The color map logic is quite complex and differs from module to module.
   * Please see the documentation in the `usePinned` composable for more details.
   */
  REGENERATE_COLOR_MAP = 'regenerate-color-map',

  /**
   * In some cases, we want to trigger case specific color map logic, for example:
   * - we want to pin the reference in Portfolio Construction Reconciliation only after the color map is updated
   */
  TRIGGER_CASE_SPECIFIC_COLOR_MAP_LOGIC = 'trigger-case-specific-color-map-logic',

  USER_UPDATED_ITEM_WEIGHT = 'user-updated-item-weight',
  TRIGGER_NOTIONAL_UPDATE = 'update-item-notional',
  TRIGGER_DIVISOR_UPDATE = 'update-basket-divisor',
  UPDATE_PARENT_AFTER_TOGGLING_SUSPENDED_OR_DELETED = 'update-parent-after-toggling-suspended-or-deleted',
  SAVE_PORTFOLIO = 'save-portfolio',
  DISCARD_CHANGES = 'discard-changes',
  RESET_ITEM_UNDER_ANALYSIS = 'reset-item-under-analysis',
  SET_WEIGHTING_SCHEME = 'set-weighting-scheme',
  TOGGLE_EXPAND_ALL_SIDEBAR_SUBPORTFOLIO_ROWS = 'TOGGLE-EXPAND-ALL-SIDEBAR-SUBPORTFOLIO-ROWS',
}

export enum BarraEvents {
  FACTOR_GROUP_CHANGE = 'factor-group-change',
}

export enum ClusteringAnalysisEvents {
  CALCULATE = 'pca:calculate',
}
export type ClusteringAnalysisCalculateEvent = Record<ClusteringAnalysisEvents.CALCULATE, IPcaOption>;

export enum ModalEvents {
  OPEN_MODAL = 'OPEN-MODAL',
}

export enum StrategyFactsheetEvents {
  SAVE_ATTRIBUTES = 'factsheet:save-attributes',
  CLEAR_ATTRIBUTES = 'factsheet:clear-attributes',
  CANCEL_EDIT_ATTRIBUTES = 'factsheet:cancel-edit-attributes',
}

export enum FactorDecompositionEvents {
  CREATE_NEW_REGRESSION = 'create-new-regression',
  SAVE_EXISTING_REGRESSION = 'save-existing-regression',
  DELETE_REGRESSION = 'delete-regression',

  CURRENT_REGRESSION_HAS_CHANGES = 'current-regression-has-changes',
  SHOW_EDIT_REGRESSION_MODAL = 'show-edit-regression-modal',
  EDITED_REGRESSION_SAVED = 'edited-regression-saved',
}

export type SaveExistingRegressionEvent = Record<
  FactorDecompositionEvents.SAVE_EXISTING_REGRESSION,
  {
    regression: IRegressionResponse;
    shouldReplaceCurrentRegressionAfterUpdate?: boolean;
  }
>;

export type CreateNewRegressionEvent = Record<
  FactorDecompositionEvents.CREATE_NEW_REGRESSION,
  {
    copy: PartialRegressionSetDTO;
  }
>;

export type DeleteRegressionEvent = Record<
  FactorDecompositionEvents.DELETE_REGRESSION,
  {
    regression: IRegressionResponse;
  }
>;

export type ShowEditRegressionModalEvent = Record<
  FactorDecompositionEvents.SHOW_EDIT_REGRESSION_MODAL,
  {
    regression: IRegressionResponse;
    createCloneOnClose?: boolean;
    showSelectedRegressorsTab?: boolean;
  }
>;

export type VoidRegressionEvent = Record<
  FactorDecompositionEvents.CURRENT_REGRESSION_HAS_CHANGES | FactorDecompositionEvents.EDITED_REGRESSION_SAVED,
  void
>;

export type StrategyFactsheetParameter = Record<StrategyFactsheetEvents, void>;

export type CheckBeforeCalculateDraftEvent = Record<
  PortfolioControllerEvents.CHECK_BEFORE_CALCULATE_DRAFT,
  CheckBeforeCalculateDraftParams | void
>;
export type NavigateToCellEvent = Record<
  PortfolioControllerEvents.NAVIGATE_TO_CELL,
  { treeId: string; col: 'unit' | 'weight'; direction?: 'up' | 'down' }
>;
export type TreeIdParameter = Record<
  PortfolioControllerEvents.FOCUS | PortfolioControllerEvents.OPEN_SUBPORTFOLIO | PortfolioControllerEvents.RESCALE_UP,
  { treeId: string; col?: 'unit' | 'weight' }
>;
export type UpdateRowChildrenEvent = Record<
  PortfolioControllerEvents.UPDATE_CHILDREN,
  { parentId: string; newChildren: ISidebarRow[] }
>;
export type RescaleDownParameter = Record<
  PortfolioControllerEvents.RESCALE_DOWN,
  { parentTreeId: string; weightingMultiplier: number }
>;
export type TogglePinEvent = Record<
  PortfolioControllerEvents.TOGGLE_PIN,
  {
    id: string;
    row: ISidebarRow;
    component: IPortfolioTreeSubportfolio | IPortfolioTreeStrategy;
  }
>;
export type VoidParameter = Record<
  | PortfolioControllerEvents.CLEAR_PINNED
  | PortfolioControllerEvents.TRIGGER_CASE_SPECIFIC_COLOR_MAP_LOGIC
  | GlobalEventTypes.AI_REPORT_UPDATED,
  void
>;

export type OpenStaticDataModalEvent = Record<
  GlobalEventTypes.OPEN_STATIC_DATA_MODAL,
  {
    portfolioTreeId?: string;
    timerId?: number;
    shouldEdit?: boolean;
    isPointerPortfolio?: boolean;
    isNewSubportfolio?: boolean;
  }
>;

export type RegenerateColorMapEvent = Record<
  PortfolioControllerEvents.REGENERATE_COLOR_MAP,
  {
    itemsToRepin?: { treeId: string; code?: string; color: string }[];
    keepColorMap?: boolean;
    newItemUnderAnalysis?: IItemUnderAnalysis | null;
    oldItemUnderAnalysis?: IItemUnderAnalysis | null;
  }
>;

export type OpenInspectPerformanceContributionBackfillModalEvent = Record<
  GlobalEventTypes.OPEN_INSPECT_PERFORMANCE_ATTRIBUTION_BACKFILL_MODAL,
  {
    portfolioId: number;
    code: string;
  }
>;

export type OpenEditClientRequestModalEvent = Record<GlobalEventTypes.OPEN_EDIT_CLIENT_REQUEST_MODAL, { id: string }>;

export type OpenBatchEditClientRequestDetailProviderModalEvent = Record<
  GlobalEventTypes.OPEN_BATCH_EDIT_CLIENT_REQUEST_DETAIL_PROVIDER_MODAL,
  { constituentStatusIds: string[] }
>;

export type OpenBatchEditClientRequestDetailConstituentStatusModalEvent = Record<
  GlobalEventTypes.OPEN_BATCH_EDIT_CLIENT_REQUEST_DETAIL_CONSTITUENT_STATUS_MODAL,
  { constituentStatusIds: string[] }
>;

export type OpenBatchEditClientRequestDetailValidationStatusModalEvent = Record<
  GlobalEventTypes.OPEN_BATCH_EDIT_CLIENT_REQUEST_DETAIL_VALIDATION_STATUS_MODAL,
  { constituentStatusIds: string[] }
>;

export type OpenBatchEditClientRequestDetailReleaseStatusModalEvent = Record<
  GlobalEventTypes.OPEN_BATCH_EDIT_CLIENT_REQUEST_DETAIL_RELEASE_STATUS_MODAL,
  { id: string; clientRequestDetailIds: string[] }
>;

export type OpenEditPrivateTrackModalEvent = Record<
  GlobalEventTypes.OPEN_EDIT_PRIVATE_TRACK_MODAL,
  { trackIdList: string[] }
>;

export type OpenKnowledgeBaseMultiMediaModalEvent = Record<
  GlobalEventTypes.OPEN_KNOWLEDGE_BASE_MULTI_MEDIA_MODAL,
  { content: string }
>;

export type ModalParamValues = {
  id: string;
  shouldRedirectToUniverseOnCancel?: boolean;
  options?: Required<{
    availableTabs: Set<DSBTabOption>;
  }>;
};

export type ModalIdParameter = Record<ModalEvents.OPEN_MODAL, ModalParamValues>;

export type PortfolioControllerEventBusEvents =
  | NavigateToCellEvent
  | CheckBeforeCalculateDraftEvent
  | TreeIdParameter
  | UpdateRowChildrenEvent
  | TogglePinEvent
  | VoidParameter
  | ModalIdParameter;

const PortfolioControllerEventBusKey: InjectionKey<Emitter<PortfolioControllerEventBusEvents>> =
  Symbol('PortfolioControllerEventBus');

export default function (): {
  PortfolioControllerEventBusKey: InjectionKey<Emitter<PortfolioControllerEventBusEvents>>;
} {
  return {
    PortfolioControllerEventBusKey,
  };
}

export type BarraEventBusEvents = Record<BarraEvents.FACTOR_GROUP_CHANGE, { barraGroup: BarraFactorGroupConstants[] }>;
